/**
 * <s-product-gallery> — PDP main-image track with thumbnail navigation.
 *
 * Thumbnails are buttons using the Invoker Commands API:
 *   <button commandfor="<gallery id>" command="--go-to" data-gallery-thumb data-index="N">
 * The gallery scrolls its snap track to slide N and keeps aria-current on the
 * matching thumbnail in sync, including when shoppers swipe the track.
 *
 * Required markup:
 *   [data-gallery-track]                 — horizontal snap-scrolling track.
 *   [data-gallery-slide][data-index="N"] — one full-width slide per media.
 *   [data-gallery-thumb][data-index="N"] — thumbnail command buttons.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', this.#handleCommand, { signal: this.#ac.signal });

    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setCurrent(Number(entry.target.dataset.index));
        }
      },
      { root: track, threshold: 0.6 }
    );

    for (const slide of track.querySelectorAll('[data-gallery-slide]')) {
      this.#observer.observe(slide);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleCommand = (event) => {
    if (event.command !== '--go-to') return;

    const index = Number(event.source?.dataset.index);
    if (Number.isNaN(index)) return;

    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollTo({ left: direction * index * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setCurrent(index);
  };

  #setCurrent(index) {
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.index) === index));
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
